import { ArrowRight, Lock, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import type { OtcPair, OtcQuote } from "../../types/cryptoSwaps.types";
import { AssetAmount } from "../shared/AssetAmount";
import { AssetAvatar } from "../shared/CryptoSwapBits";
import { FeesValue } from "../shared/FeesValue";
import { QuoteTimerBar } from "./QuoteTimerBar";

export type TicketNotice =
  | { kind: "expired" }
  | { kind: "manual" }
  | { kind: "failed"; message: string; retryable: boolean };

export interface QuoteTicketProps {
  pair: OtcPair;
  quote: OtcQuote | null;
  isPricing: boolean;
  clockOffsetMs: number;
  networkLabel: string;
  /** Replaces the disabled-button reason: it says what happened and offers the way out. */
  notice: TicketNotice | null;
  /** Why the button is disabled, in one sentence. Null when it is not. */
  blockMessage: string | null;
  canReview: boolean;
  onReview: () => void;
  onRequote: () => void;
  className?: string;
}

/** The price as a receipt: `--ticket` surface, `.dark` scope so semantic colours stay legible on it. */
export function QuoteTicket({
  pair,
  quote,
  isPricing,
  clockOffsetMs,
  networkLabel,
  notice,
  blockMessage,
  canReview,
  onReview,
  onRequote,
  className,
}: QuoteTicketProps) {
  const { t } = useTranslations("cryptoSwaps");
  const format = useAmountFormatter(pair);
  const dimmed = isPricing || notice?.kind === "expired";

  return (
    <aside aria-label={t("quote_ticket_title")} className={cn("flex flex-col gap-3", className)}>
      <div className="overflow-hidden rounded-3xl border border-border shadow-[var(--shadow-elev)]">
        <div className="dark bg-ticket text-ticket-foreground">
          <div className="flex flex-col gap-5 p-6">
            <QuoteTimerBar
              key={quote?.id ?? "none"}
              label={t("quote_ticket_title")}
              quote={quote}
              isPricing={isPricing}
              clockOffsetMs={clockOffsetMs}
            />

            <div className={cn("flex flex-col gap-1.5 transition-opacity", dimmed && "opacity-60")}>
              <span className="text-[13px] text-muted-foreground">{t("quote_summary_receive")}</span>
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <AssetAvatar
                  asset={quote?.receive.currency ?? pair.receive_currency}
                  kind="ONCHAIN"
                  size="md"
                />
                {quote ? (
                  <AssetAmount
                    value={quote.receive.amount}
                    currency={quote.receive.currency}
                    pair={pair}
                    className="text-[44px] leading-none font-semibold tracking-tight"
                  />
                ) : (
                  <span className="font-mono text-[44px] leading-none font-semibold text-muted-foreground">
                    —
                  </span>
                )}
                <span className="font-mono text-base font-medium text-success-container-foreground">
                  {quote?.receive.currency ?? pair.receive_currency}
                </span>
              </span>
            </div>

            {notice && <NoticePanel notice={notice} onRequote={onRequote} />}
          </div>

          <div aria-hidden="true" className="mx-6 border-t border-dashed border-border" />

          <div className="flex flex-col gap-5 p-6">
            <dl
              className={cn(
                "m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-[13px] transition-opacity",
                dimmed && "opacity-60",
              )}
            >
              <TicketRow label={t("quote_summary_price")} mono={!!quote}>
                {quote
                  ? t("price_line", {
                      asset: quote.receive.currency,
                      price: format(quote.price.value, quote.price.currency),
                      currency: quote.price.currency,
                    })
                  : "—"}
              </TicketRow>
              <TicketRow label={t("quote_summary_pay")} mono={!!quote}>
                {quote ? (
                  <>
                    <AssetAmount
                      value={quote.send.amount}
                      currency={quote.send.currency}
                      pair={pair}
                      size="sm"
                      className="text-[13px] font-semibold"
                    />{" "}
                    {quote.send.currency}
                  </>
                ) : (
                  "—"
                )}
              </TicketRow>
              <TicketRow label={t("quote_summary_fees")}>
                {quote ? <FeesValue fees={quote.fees} pair={pair} /> : "—"}
              </TicketRow>
              <TicketRow label={t("quote_summary_network")}>{networkLabel || "—"}</TicketRow>
            </dl>

            <div className="flex flex-col gap-2">
              <ColocaButton
                type="button"
                variant="primary"
                size="lg"
                iconTrailing={ArrowRight}
                className="h-12 w-full"
                disabled={!canReview}
                onClick={onReview}
              >
                {t("quote_submit")}
              </ColocaButton>
              {blockMessage && !notice && (
                <p className="text-center text-xs leading-relaxed text-muted-foreground">{blockMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="flex gap-2 px-1 text-xs leading-relaxed text-muted-foreground">
        <Lock aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
        {t("quote_submit_footnote")}
      </p>
    </aside>
  );
}

export function NoticePanel({ notice, onRequote }: { notice: TicketNotice; onRequote: () => void }) {
  const { t } = useTranslations("cryptoSwaps");

  const content =
    notice.kind === "expired"
      ? {
          title: t("quote_expired_title"),
          text: t("quote_expired_message"),
          action: t("quote_expired_action"),
        }
      : notice.kind === "manual"
        ? { title: null, text: t("quote_manual_title"), action: t("quote_manual_action") }
        : { title: null, text: notice.message, action: notice.retryable ? t("quote_retry_action") : null };

  return (
    <div role="alert" className="flex flex-col gap-3 rounded-2xl border border-border bg-muted p-4">
      {content.title && <p className="text-base font-semibold text-destructive">{content.title}</p>}
      <p className="text-[13px] leading-relaxed text-muted-foreground">{content.text}</p>
      {content.action && (
        <ColocaButton
          type="button"
          variant="primary"
          icon={RefreshCw}
          className="self-start"
          onClick={onRequote}
        >
          {content.action}
        </ColocaButton>
      )}
    </div>
  );
}

export function TicketRow({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("m-0 text-right", mono && "font-mono tabular-nums")}>{children}</dd>
    </>
  );
}
